import Form from "@/components/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import ClientSelect from "@/components/ClientSelect";
import DatePicker from "@/components/DatePicker";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import PageContent from "@/components/PageContent";
import ProjectTypeSelect from "@/components/ProjectTypeSelect";
import SaveButton from "@/components/SaveButton";
import ServicesAndGoodsSelect from "@/components/ServicesAndGoodsSelect";
import { Input } from "@/components/ui/input";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { isRequired } from "@/lib/form";
import {
  project as projectSchema,
  updateRoleBasedProject,
  updateDepartmentBasedProject,
} from "@/schemas/project";
import { ApiError } from "@/services/api";
import {
  ReadProject,
  UpdateRoleBasedProject,
  UpdateDepartmentBasedProject,
} from "@/types/project";
import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useMemo } from "react";
import { defaultUpdateValues } from "../defaultValues";
import EndDateButton from "./EndDateButton";
import InvoicingCompanySelect from "@/components/InvoicingCompanySelect";
import ProjectPrimaryManagerSelect from "./ProjectPrimaryManagerSelect";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import CheckboxField from "@/components/CheckboxField";
import PayrollTypeSelect from "@/components/PayrollTypeSelect";
import { PROJECT_DEPENDENCY_ROLE } from "@/schemas/commonFields";
import { isProjectRoleDependent } from "@/modules/projects/lib/isProjectRoleDependent";

interface ProjectFormProps {
  project?: Partial<ReadProject>;
  onSubmit: UseMutateAsyncFunction<
    ReadProject,
    ApiError,
    UpdateRoleBasedProject | UpdateDepartmentBasedProject
  >;
  disabled?: boolean;
}

const ProjectEditorForm = ({
  project = defaultUpdateValues,
  onSubmit,
  disabled,
}: ProjectFormProps) => {
  const { t } = useTranslation();
  const { can_update } = useModulePermissions();
  const isRoleDependent = isProjectRoleDependent(project);

  const isDisabled = useMemo(
    () => disabled || !can_update,
    [disabled, can_update],
  );

  const updateSchema = useMemo(
    () =>
      isRoleDependent ? updateRoleBasedProject : updateDepartmentBasedProject,
    [isRoleDependent],
  );

  const form = useForm<UpdateRoleBasedProject | UpdateDepartmentBasedProject>({
    resolver: zodResolver(updateSchema),
    defaultValues: project,
    disabled: isDisabled,
  });

  useFormUnsavedChangesWarning(form);

  const handleSubmit = async (
    values: UpdateRoleBasedProject | UpdateDepartmentBasedProject,
  ) => {
    await onSubmit(values);
  };

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <PageContent footer={<SaveButton disabled={isDisabled} />}>
        <Fieldset
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          title={t("projects.title.basicData")}
        >
          <Field
            name="name"
            label={t("projects.fields.name")}
            required={isRequired(projectSchema.shape.name)}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={isDisabled} />
            )}
          />
          <Field
            name="primary_manager_id"
            label={t("projects.fields.primary_manager")}
            required={isRequired(projectSchema.shape.primary_manager_id)}
            render={({ field }) => (
              <ProjectPrimaryManagerSelect
                className="w-full"
                defaultItem={{
                  id: project.primary_manager_id,
                  full_name: project.primary_manager,
                  is_active: project.primary_manager_active,
                }}
                {...field}
                disabled={isDisabled}
              />
            )}
          />

          <Field
            name="client_id"
            label={t("projects.fields.client")}
            required={isRequired(projectSchema.shape.client_id)}
            render={({ field }) => (
              <ClientSelect
                className="w-full"
                defaultItem={{ id: project.client_id, name: project.client }}
                {...field}
                disabled
              />
            )}
          />
          <Field
            name="project_type_id"
            label={t("projects.fields.project_type")}
            required={isRequired(projectSchema.shape.project_type_id)}
            render={({ field }) => (
              <ProjectTypeSelect
                className="w-full"
                defaultItem={{
                  id: project.project_type_id,
                  name: project.project_type,
                }}
                projectDependsOn={project.project_depends_on}
                {...field}
                disabled
              />
            )}
          />
          <Field
            name="invoicing_company_id"
            label={t("projects.fields.invoicing_company")}
            required={isRequired(projectSchema.shape.invoicing_company_id)}
            render={({ field }) => (
              <InvoicingCompanySelect
                defaultItem={{
                  id: project.invoicing_company_id,
                  name: project.invoicing_company,
                }}
                {...field}
                disabled={isDisabled}
              />
            )}
          />
          <div className="flex items-start gap-4">
            <Field
              name="start_date"
              label={t("projects.fields.start_date")}
              required={isRequired(projectSchema.shape.start_date)}
              wrapperClassName="flex-1"
              render={({ field }) => (
                <DatePicker className="w-full" {...field} disabled />
              )}
            />
            <Field
              name="end_date"
              label={t("projects.fields.end_date")}
              required={isRequired(projectSchema.shape.end_date)}
              wrapperClassName="flex-1"
              render={({ field }) => (
                <EndDateButton {...field} disabled={isDisabled} />
              )}
            />
          </div>
          <Field
            name="services_and_goods_id"
            label={t("projects.fields.services_and_goods")}
            required={isRequired(projectSchema.shape.services_and_goods_id)}
            render={({ field }) => (
              <ServicesAndGoodsSelect
                className="w-full"
                defaultItem={{
                  id: project.services_and_goods_id,
                  name: project.services_and_goods,
                  code: project.services_and_goods_code,
                  invoice_description:
                    project.services_and_goods_invoice_description,
                }}
                {...field}
                disabled={isDisabled}
              />
            )}
          />
          <Field
            name="crs_number"
            label={t("projects.fields.crs_number")}
            required={isRequired(projectSchema.shape.crs_number)}
            render={({ field }) => (
              <Input className="w-full" {...field} disabled={isDisabled} />
            )}
          />
          <Field
            name="status"
            label={t("projects.fields.status")}
            render={({ field }) => (
              <Input
                disabled
                className="w-full"
                value={t(`projectStatuses.${field.value}`)}
              />
            )}
          />
          {isRoleDependent && (
            <Field
              name="payroll_type_id"
              label={t("projects.fields.payroll_type")}
              render={({ field }) => (
                <PayrollTypeSelect
                  {...field}
                  className="w-full"
                  enabled={isRoleDependent}
                  defaultItem={{
                    id: project.payroll_type_id,
                    name: project.payroll_type,
                  }}
                  projectDependsOn={PROJECT_DEPENDENCY_ROLE}
                  disabled={isDisabled}
                  allowClearSelection
                />
              )}
            />
          )}
        </Fieldset>

        <Fieldset
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-0"
          title={t("projects.title.settings")}
        >
          <CheckboxField
            name="geolocalization_enabled"
            label={t("projects.fields.geolocalization_enabled")}
            required={isRequired(projectSchema.shape.geolocalization_enabled)}
            disabled={isDisabled}
          />
          <CheckboxField
            name="contractor_card_required"
            label={t("projects.fields.contractor_card_required")}
            required={isRequired(projectSchema.shape.contractor_card_required)}
            disabled={isDisabled}
          />
          <CheckboxField
            name="contractor_visits_planned_duration_visible"
            label={t(
              "projects.fields.contractor_visits_planned_duration_visible",
            )}
            required={isRequired(
              projectSchema.shape.contractor_visits_planned_duration_visible,
            )}
            disabled={isDisabled}
          />
          <CheckboxField
            name="contractor_visits_editable_by_pr"
            label={t("projects.fields.contractor_visits_editable_by_pr")}
            required={isRequired(
              projectSchema.shape.contractor_visits_editable_by_pr,
            )}
            disabled={isDisabled}
          />
          <CheckboxField
            name="contractor_visits_hours_editable_by_contractor"
            label={t(
              "projects.fields.contractor_visits_hours_editable_by_contractor",
            )}
            required={isRequired(
              projectSchema.shape
                .contractor_visits_hours_editable_by_contractor,
            )}
            disabled={isDisabled}
          />
          <CheckboxField
            name="contractor_visits_confirmable_by_contractor"
            label={t(
              "projects.fields.contractor_visits_confirmable_by_contractor",
            )}
            required={isRequired(
              projectSchema.shape.contractor_visits_confirmable_by_contractor,
            )}
            disabled={isDisabled}
          />
        </Fieldset>
      </PageContent>
    </Form>
  );
};

export default ProjectEditorForm;
